import { and, eq, gte, or } from 'tio-ui/utilities';
import { concat } from '@ember/helper';
import { fn } from '@ember/helper';
import Modal from 'tio-ui/components/modal';
import Footer from 'tio-ui/components/footer';
import { LinkTo } from '@ember/routing';
import { on } from '@ember/modifier';
import { service } from '@ember/service';
import { t } from 'ember-intl';
import { tracked } from '@glimmer/tracking';
import Button from 'tio-ui/components/button';
import checkBulletListsAreSame from 'tio-employee/utils/recommendations/check-bullet-lists-are-same';
import Component from '@glimmer/component';
import { dropTask } from 'ember-concurrency';
import ForgivenessEstimatorResultInstructions from './result-instructions';
import { PhPrinter } from 'ember-phosphor-icons';
import safeFormatNumber from 'tio-common/helpers/safe-format-number';
import DownloadButton from 'tio-ui/components/download-button';
import type IntlService from 'ember-intl/services/intl';
import type RecommendationModel from 'tio-data/models/recommendation';
import type StrategyFinderService from 'tio-common/services/strategy-finder';
import type Store from '@ember-data/store';
import { Section } from 'tio-ui/components/layout';

type PlanHeader = {
  title?: string;
  bullets: string[];
  hasBullets: boolean;
};

export interface FEResultsDashboardTableSignature {
  Args: {
    isCompact?: boolean;
    programType: string;
    recommendation: RecommendationModel;
  };
  Element: HTMLElement;
  Blocks: {
    default: [];
  };
}

export default class ResultsDashboardTable extends Component<FEResultsDashboardTableSignature> {
  @service declare intl: IntlService;
  @service declare strategyFinder: StrategyFinderService;
  @service declare store: Store;

  // Provides request auth headers to DownloadButton; keeps that UI component free of any store/adapter tie.
  downloadHeaders = () =>
    (this.store.adapterFor('application') as unknown as { headers: Record<string, string> })
      .headers;

  @tracked toggleDialog = false;

  downloadAndOpenInNewTab = dropTask(async (url: string) => {
    if (!url) {
      return;
    }
    const adapter = this.store.adapterFor('application');
    const headers = adapter.headers;
    const response = await fetch(url, { headers });
    if (response.ok) {
      const blob = await response.blob();
      const fileUrl = window.URL.createObjectURL(blob);
      const link = document.createElement('a');
      link.href = fileUrl;
      link.setAttribute('target', '_blank');
      link.setAttribute('rel', 'noreferrer noopener');
      document.body.appendChild(link);
      link.click();
      document.body.removeChild(link);
    } else {
      console.error('Unable to download file due to an error.', response);
    }
  });

  get estimatedMonthlyPayment() {
    return this.args.recommendation?.estimatedMonthlyPayment;
  }

  get estimatedForgivenessAmount() {
    return this.args.recommendation?.estimatedForgivenessAmount;
  }

  get estimatedForgivenessDate() {
    return this.args.recommendation?.estimatedForgivenessDate;
  }

  get currentPlanResults() {
    return this.args.recommendation?.formattedCurrentPlanSummary;
  }

  get bestPlanResults() {
    return this.args.recommendation?.formattedBestPlanSummary;
  }

  get workflowRoute() {
    return this.args.programType === 'pslf'
      ? 'authenticated.pslf.dashboard.forgiveness-estimator'
      : this.args.programType === 'sf'
        ? 'authenticated.repaying-student-debt.repayment-strategy-finder.dashboard.questionnaire'
        : 'authenticated.idr.dashboard.forgiveness-estimator';
  }

  get verticalHeaderRows() {
    return [
      {
        title: this.intl.t('recommendations_estimator.estimated_total_to_pay'),
        dataKey: 'payoffAmount',
        formatter: 'formatMoney',
      },
      {
        title: this.intl.t('recommendations_estimator.years_left_to_repay'),
        dataKey: 'termMonths',
        formatter: 'formatTermMonths',
      },
      {
        title: this.intl.t('recommendations_estimator.monthly_payment'),
        dataKey: 'monthlyPayments',
        formatter: 'formatMoney',
        asterisk: true,
      },
      {
        title: this.intl.t('recommendations_estimator.estimated_total_interest_paid'),
        dataKey: 'totalInterestPaid',
        formatter: 'formatMoney',
      },
      {
        title: this.intl.t('recommendations_estimator.estimated_amount_forgiven'),
        subtitle: this.intl.t(
          'recommendations_estimator.estimated_amount_forgiven_includes_interest'
        ),
        dataKey: 'remLoanBalance',
        formatter: 'formatMoney',
      },
    ];
  }

  get horizontalHeaderColumns() {
    return [
      {
        title: this.bestPlanColumnTitle,
        ...this.planHeaderColumn(this.bestPlanHeaders),
      },
      {
        title: this.currentPlanColumnTitle,
        ...this.planHeaderColumn(this.currentPlanHeaders),
      },
    ];
  }

  get bestPlanColumnTitle() {
    switch (this.args.programType) {
      case 'pslf':
        return this.intl.t('recommendations_estimator.best_plan');
      case 'idr':
        return this.intl.t('idr.abbreviated');
      case 'sf':
        return this.intl.t('recommendations_estimator.top_strategy');
      default:
        return '';
    }
  }

  get currentPlanColumnTitle() {
    return this.intl.t('recommendations_estimator.current_plan');
  }

  get bestPlanHeaders() {
    return this.normalizePlanHeaders(this.args.recommendation?.bestPlanTableHeadings || []);
  }

  get currentPlanHeaders() {
    return this.normalizePlanHeaders(this.args.recommendation?.currentPlanTableHeadings || []);
  }

  planHeaderColumn(headers: PlanHeader[]) {
    const showHeaderTitle = this.showPlanHeaderTitle(headers);

    return {
      headers: headers.length > 1 && !showHeaderTitle ? headers.slice(0, 1) : headers,
      showHeaderTitle,
    };
  }

  showPlanHeaderTitle(headers: PlanHeader[]) {
    return headers.length <= 1 || !checkBulletListsAreSame(headers);
  }

  normalizePlanHeaders(headers: Record<string, unknown>[]) {
    return headers.map((header) => {
      const bullets = Array.isArray(header.bullets)
        ? header.bullets
            .filter((bullet): bullet is string => typeof bullet === 'string')
            .map((bullet) => bullet.trim())
            .filter(Boolean)
        : [];

      return {
        title: typeof header.title === 'string' ? header.title : undefined,
        bullets,
        hasBullets: bullets.length > 0,
      };
    });
  }

  get tableRowData() {
    return this.verticalHeaderRows.map((row) => {
      // @ts-expect-error: need real types
      const col1Value = this.bestPlanResults?.[row.dataKey];
      // @ts-expect-error: need real types
      const col2Value = this.currentPlanResults?.[row.dataKey];

      return {
        title: row.title,
        subtitle: row.subtitle,
        col1Data: this.formatData(row.formatter, col1Value),
        col2Data: this.formatData(row.formatter, col2Value),
        asterisk: row.asterisk,
      };
    });
  }

  formatData = (formatFunctionName: string, data: unknown) => {
    // @ts-expect-error: I have no idea
    return this[formatFunctionName]?.(data) || data;
  };

  formatMoney = (cents = []) => {
    return this.strategyFinder.formatCentsRange(cents);
  };

  formatTermMonths = (months = []) => {
    return this.strategyFinder.formatMonthsRange(months);
  };

  <template>
    <Section>
      <:header>{{t "pslf_dashboard.results.default"}}</:header>
      <:body>
        <div class="grid grid-cols-7 gap-4">
          {{#if (eq @programType "idr")}}
            <p class="col-span-7">{{t "idr.results.description" htmlSafe=true}}</p>
          {{/if}}
          <div
            class="col-span-7
              {{if (or (eq @programType 'idr') (eq @programType 'sf')) 'md:col-span-4'}}"
          >
            {{#if @recommendation.hasErrorsInCalcs}}
              <div class="m-2">
                {{t "pslf_dashboard.results.error"}}
                <LinkTo @route={{this.workflowRoute}} class="tio-copy">
                  {{#if (eq @programType "sf")}}
                    {{t "common.strategy_finder.default"}}
                  {{else}}
                    {{t "recommendations_estimator.forgiveness_estimator"}}
                  {{/if}}
                </LinkTo>
                {{concat ". " (t "pslf_dashboard.results.thank_you")}}
              </div>
            {{else}}
              {{#if (eq @programType "idr")}}
                <div class="font-semibold my-6">
                  <p>
                    {{t "idr.results.estimated_monthly_payment"}}
                    <span class="ml-1 text-midnight">
                      {{#if (gte this.estimatedMonthlyPayment 0)}}
                        {{safeFormatNumber
                          this.estimatedMonthlyPayment
                          style="currency"
                          currency="USD"
                          maximumFractionDigits=0
                        }}
                      {{/if}}
                    </span>
                  </p>
                  <p>
                    {{t "idr.results.estimated_loan_forgiveness"}}
                    <span class="ml-1 text-midnight">
                      {{#if (gte this.estimatedForgivenessAmount 0)}}
                        {{safeFormatNumber
                          this.estimatedForgivenessAmount
                          style="currency"
                          currency="USD"
                          maximumFractionDigits=0
                        }}
                      {{/if}}
                    </span>
                  </p>
                </div>
              {{else}}
                <p class="mb-6">{{t "pslf_dashboard.results.description"}}</p>
                <div class="font-semibold mb-6">
                  <p class="mb-1">
                    {{t "pslf_dashboard.results.estimated_monthly_payment_is"}}
                    <span class="ml-1 text-midnight">
                      {{#if (gte this.estimatedMonthlyPayment 0)}}
                        {{safeFormatNumber
                          this.estimatedMonthlyPayment
                          style="currency"
                          currency="USD"
                        }}*
                      {{/if}}
                    </span>
                  </p>
                  <p class="mb-1">
                    {{t "pslf_dashboard.results.estimated_loan_forgiveness_amount_is"}}
                    <span class="ml-1 text-midnight">
                      {{#if (gte this.estimatedForgivenessAmount 0)}}
                        {{safeFormatNumber
                          this.estimatedForgivenessAmount
                          style="currency"
                          currency="USD"
                        }}
                      {{/if}}
                    </span>
                  </p>
                  <p class="mb-1">
                    {{t "pslf_dashboard.results.estimated_loan_forgiveness_date_is"}}
                    <span class="ml-1 text-midnight">
                      {{this.estimatedForgivenessDate}}
                    </span>
                  </p>
                </div>
              {{/if}}

              <table class="pslf-dashboard-results-table {{if @isCompact 'w-full table-fixed'}}">
                <thead>
                  <tr>
                    <th></th>
                    {{#each this.horizontalHeaderColumns as |column|}}
                      <th scope="col" class="align-top {{if @isCompact 'px-2 py-3 sm:px-3' 'p-4'}}">
                        <div class="text-center font-semibold">{{column.title}}</div>
                        {{#if column.headers.length}}
                          <div
                            class="mt-2 text-left font-normal tio-fg-secondary leading-snug
                              {{if @isCompact 'text-xs sm:text-sm' 'text-sm'}}"
                          >
                            {{#each column.headers as |header|}}
                              {{#if (and column.showHeaderTitle header.title)}}
                                <p class="mb-1 font-semibold">{{header.title}}</p>
                              {{/if}}
                              {{#if header.hasBullets}}
                                <ul
                                  class="list-disc mb-2
                                    {{if @isCompact 'list-outside pl-4' 'list-inside'}}"
                                >
                                  {{#each header.bullets as |bullet|}}
                                    <li>{{bullet}}</li>
                                  {{/each}}
                                </ul>
                              {{/if}}
                            {{/each}}
                          </div>
                        {{/if}}
                      </th>
                    {{/each}}
                  </tr>
                </thead>
                <tbody>
                  {{#each this.tableRowData as |row|}}
                    <tr>
                      <td
                        class="text-left align-top tio-fg-secondary
                          {{if @isCompact 'px-2 py-3 sm:px-3' 'p-4'}}"
                      >
                        {{row.title}}
                        {{#if row.subtitle}}
                          <div class="tio-text-xs-normal">{{row.subtitle}}</div>
                        {{/if}}
                      </td>
                      <td
                        class="text-center align-top tio-bg-info-soft
                          {{if @isCompact 'px-2 py-3 sm:px-3' 'p-4'}}"
                      >{{row.col1Data}}
                        {{#if (and row.asterisk (eq @programType "pslf"))}}
                          <span>*</span>
                        {{/if}}
                      </td>
                      <td class="text-center align-top {{if @isCompact 'px-2 py-3 sm:px-3' 'p-4'}}">
                        {{row.col2Data}}
                      </td>
                    </tr>
                  {{/each}}
                </tbody>
              </table>
              <Button
                @appearance="minimal"
                @intent="primary"
                {{on "click" (fn (mut this.toggleDialog) true)}}
                class="my-4 underline"
              >
                {{t "recommendations_estimator.step_by_step_instructions"}}
              </Button>
              <div class="flex gap-5">
                <Button
                  @appearance="outlined"
                  @intent="primary"
                  @isRunning={{this.downloadAndOpenInNewTab.isRunning}}
                  {{on
                    "click"
                    (fn this.downloadAndOpenInNewTab.perform @recommendation.downloadUrl)
                  }}
                  class="w-full max-w-48"
                >
                  <PhPrinter @size="16" class="mr-2" />
                  {{t "common.print"}}
                </Button>
                <DownloadButton
                  @authHeaders={{this.downloadHeaders}}
                  @url={{@recommendation.downloadUrl}}
                  @filename={{@recommendation.downloadFilename}}
                  class="w-full max-w-48"
                />
              </div>
            {{/if}}
          </div>
          {{yield}}
        </div>
      </:body>
    </Section>

    <Modal
      @isOpen={{this.toggleDialog}}
      @onClose={{fn (mut this.toggleDialog) false}}
      class="min-h-64 lg:min-w-200!"
      @size="2xl"
    >
      <:header>{{t "recommendations_estimator.step_by_step_instructions"}}</:header>
      <:body>
        <ForgivenessEstimatorResultInstructions @recommendation={{@recommendation}} />
      </:body>
      <:footer>
        <Footer
          @isRunning={{false}}
          @onSubmit={{fn (mut this.toggleDialog) false}}
          @submitText={{t "common.close"}}
        />
      </:footer>
    </Modal>
  </template>
}
